import { Controller } from "@hotwired/stimulus";
import { lineSpinner } from "ldrs";

// #showOverlay renders <l-line-spinner>. form_submit_controller registers it
// for app pages, but the landing bundle doesn't load that controller.
// register() skips a tag that's already defined.
lineSpinner.register();

export default class extends Controller {
  static values = {
    current: String,
    confirmTitle: String,
    confirmMessage: String,
    confirmLabel: String,
    switchingLabel: String,
  };

  switch(e) {
    const locale = e.currentTarget.dataset.locale;
    if (!locale || locale === this.currentValue) return;

    const url = e.currentTarget.dataset.url;

    // Immediate feedback the moment the tap registers (M3 states: never
    // leave an action with dead silence) — button dims to "pressed" right
    // away, before the popup even starts closing.
    e.currentTarget.classList.add("top-nav__lang--pending");

    // Closing the popup and starting the locale PATCH used to run back to
    // back (await close, then fetch), stacking their durations into one
    // long silent gap before Turbo.visit. Run them in parallel instead —
    // the fetch is already in flight while the popup animates out.
    const closePromise = this.#closeSettingsPopover();

    // Mid-wizard the switch reloads the page and wipes unsaved form state —
    // text fields, selections, and file uploads can't survive it. Confirm first.
    const inWizard = !!document.querySelector("[data-controller~='project-wizard']");
    if (inWizard) {
      closePromise.then(() => {
        window.dispatchEvent(new CustomEvent("confirm-dialog:open", {
          detail: {
            title: this.confirmTitleValue,
            message: this.confirmMessageValue,
            confirmLabel: this.confirmLabelValue,
            onConfirm: () => this.#go(url, Promise.resolve()),
          },
        }));
      });
      return;
    }

    this.#go(url, closePromise);
  }

  #closeSettingsPopover() {
    const dialog = document.querySelector(".settings-popover__dialog--open");
    if (!dialog) return Promise.resolve();

    window.dispatchEvent(new CustomEvent("settings-popover:close"));

    // The transition lives on __panel, not the outer __dialog wrapper —
    // checking the wrapper's own transitionDuration always reads 0s.
    const panel = dialog.querySelector(".settings-popover__panel");

    return new Promise((resolve) => {
      const duration = parseFloat(getComputedStyle(panel).transitionDuration) * 1000;
      if (duration > 0) {
        panel.addEventListener("transitionend", () => resolve(), { once: true });
      } else {
        resolve();
      }
    });
  }

  #go(url, closePromise) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    // A tiny "pending" tint on the tapped button isn't enough on a slow
    // connection: on mobile the popup that held that button is gone
    // ~250ms later, leaving nothing on screen for the rest of a multi-
    // second PATCH. A full-screen backdrop + spinner (same pattern as
    // the video-render submit overlay) stays visible the whole time,
    // wherever the tap originated.
    this.#showOverlay();

    const fetchPromise = fetch(url, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
    });

    // On a fast connection the PATCH resolves near-instantly, and the
    // overlay would flash off before its own text is even readable — a
    // floor on how long it stays up regardless of how fast the network is.
    const minVisible = new Promise((resolve) => setTimeout(resolve, 500));

    Promise.all([fetchPromise, closePromise, minVisible]).then(() => {
      Turbo.visit(window.location.href, { action: "replace" });
    }).catch(() => {
      // Network failure — page never navigates away, so the loading
      // state we set at tap time would otherwise spin forever.
      this.#hideOverlay();
      document.querySelector(".top-nav__lang--pending")?.classList.remove("top-nav__lang--pending");
    });
  }

  #showOverlay() {
    const overlay = document.createElement("div");
    overlay.className = "submit-overlay";
    overlay.setAttribute("aria-live", "assertive");
    overlay.setAttribute("role", "alert");

    const content = document.createElement("div");
    content.className = "submit-overlay__content";

    const spinner = document.createElement("l-line-spinner");
    spinner.setAttribute("size", "40");
    spinner.setAttribute("stroke", "3");
    spinner.setAttribute("speed", "1");
    spinner.setAttribute("color", "#fff");

    const title = document.createElement("h2");
    title.className = "submit-overlay__title";
    title.textContent = this.switchingLabelValue || "";

    content.append(spinner, title);
    overlay.appendChild(content);

    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add("submit-overlay--visible"));
    this.overlay = overlay;
  }

  #hideOverlay() {
    this.overlay?.remove();
    this.overlay = null;
  }
}
